import { useEffect, useRef } from "react";
import {
  MAIL_STORAGE_INBOX_SYNC_DEFER_MS,
} from "@/lib/constants/mailStorage";
import {
  fetchNewMailToLocalStorage,
  syncInboxLocalStorage,
} from "@/lib/storage/inboxLocalStorageSync";
import {
  markInboxArchiveInflight,
  markInboxArchiveKicked,
  resetInboxArchiveSession,
} from "@/lib/storage/inboxArchiveSession";
import { navFolderSlot } from "@/lib/mail/folderNavSections";
import {
  MAIL_NEW_MAIL_EVENT,
  type MailNewMailDetail,
} from "@/lib/mail/mailNewMailEvents";

/**
 * Déclenche l’archivage INBOX serveur une fois par session compte (fond, non bloquant).
 * Différé tant que l’onglet Mail n’est pas visible (évite de saturer Apache / le nav).
 */
export function useInboxLocalStorageSync(
  accountId: number | undefined,
  folder: string,
  uids: number[],
  enabled = true
): void {
  const uidsRef = useRef(uids);
  uidsRef.current = uids;
  const prevAccountRef = useRef<number | undefined>(undefined);
  const isInbox = navFolderSlot(folder, "") === "inbox";

  useEffect(() => {
    if (!enabled || accountId == null || !isInbox) {
      return;
    }

    if (!markInboxArchiveKicked(accountId)) {
      return;
    }

    let cancelled = false;
    let timer: number | null = null;
    let started = false;

    const kick = () => {
      if (cancelled || accountId == null || started) return;
      if (document.visibilityState !== "visible") return;
      started = true;
      markInboxArchiveInflight(accountId, true);
      void syncInboxLocalStorage(accountId, folder, uidsRef.current).finally(() => {
        markInboxArchiveInflight(accountId, false);
      });
    };

    const schedule = () => {
      if (started || timer != null) return;
      timer = window.setTimeout(kick, MAIL_STORAGE_INBOX_SYNC_DEFER_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !started) {
        schedule();
      }
    };

    if (document.visibilityState === "visible") {
      schedule();
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [accountId, enabled, folder, isInbox]);

  useEffect(() => {
    if (accountId == null) return;
    const prev = prevAccountRef.current;
    if (prev != null && prev !== accountId) {
      resetInboxArchiveSession(prev);
    }
    prevAccountRef.current = accountId;
  }, [accountId]);

  useEffect(() => {
    if (!enabled || accountId == null || !isInbox) return;

    const onNewMail = (event: Event) => {
      const detail = (event as CustomEvent<MailNewMailDetail>).detail;
      if (!detail || detail.accountId !== accountId || detail.folder !== folder) return;
      for (const uid of detail.uids ?? []) {
        void fetchNewMailToLocalStorage(accountId, folder, uid);
      }
    };

    window.addEventListener(MAIL_NEW_MAIL_EVENT, onNewMail);
    return () => window.removeEventListener(MAIL_NEW_MAIL_EVENT, onNewMail);
  }, [accountId, enabled, folder, isInbox]);
}
